import React from "react";
import { Card } from "react-bulma-components";
import { GitCompare, Scale } from "lucide-react";
import { motion } from "framer-motion";
import Image from "../common/Image";

interface PCListItemProps {
  pc: Compound;
  layoutGrid: boolean;
  handleViewDetails: (id: string) => void;
  handleAddToCompare: (id: string) => void;
  selectedPCIdsToCompare: string[];
  showCosts?: boolean;
}

const PCListItem: React.FC<PCListItemProps> = ({
  pc,
  layoutGrid,
  handleViewDetails,
  handleAddToCompare,
  selectedPCIdsToCompare,
  showCosts = true,
}) => {
  const stockWarningThreshold = 5;
  const stockLabel =
    pc.available_units === 0
      ? "Consultar"
      : pc.available_units <= stockWarningThreshold
        ? "Últimas Unidades"
        : "Disponivel";
  return (
    <Card
      onClick={() => handleViewDetails(pc.id)}
      className={`pc-configuration hover:shadow-lg is-clickable transition-shadow is-rounded ${
        layoutGrid ? "" : "flex grow-2"
      }`}
    >
      <Card.Header className={layoutGrid ? "" : "flex-row sm:flex-col"}>
        <div className="image-container w-full">
          <Image
            height={layoutGrid ? 200 : 100}
            src="https://placehold.co/400"
            dataSrc={
              pc.image && pc.image.trim() !== ""
                ? `${pc.image.trim()}`
                : "https://placehold.co/400"
            }
            alt={pc.name}
            sizes="400x400"
            version={pc.version}
            loading="progressive"
            className={`object-cover w-full  ${
              layoutGrid ? "h-48" : "sm:h-full sm:max-h-[200px]"
            }`}
          />
        </div>
      </Card.Header>
      <Card.Content
        className={
          layoutGrid ? "" : "flex flex-col items-start justify-between"
        }
      >
        <div className="block justify-between items-start">
          <h4
            className={`text-stone-900 is-size-5 ${layoutGrid ? "line-clamp-2" : ""}`}
          >
            {pc.id}
          </h4>
          <p className={`text-gray-600 text-xs`}>{pc.description}</p>
        </div>
        {stockLabel && (
          <div className={layoutGrid ? "" : "absolute top-2 right-2"}>
            <span
              className={`tag is-light text-xs font-bold ${
                pc.available_units === 0
                  ? "is-danger"
                  : pc.available_units <= stockWarningThreshold
                    ? "is-warning"
                    : "is-success"
              }`}
            >
              {stockLabel}
            </span>
          </div>
        )}
        {!pc.image && (
          <div
            className={`${
              layoutGrid ? "flex justify-between items-start gap-2" : ""
            }`}
          >
            <div
              className={`text-gray-400 mb-6 ${layoutGrid ? "line-clamp-3" : ""}`}
            >
              {pc.description}
            </div>
          </div>
        )}
        <div
          className={
            layoutGrid
              ? "space-y-2"
              : "flex flex-col sm:flex-row sm:items-center sm:gap-4"
          }
        >
          <div className={`w-full ${layoutGrid ? "gap-2" : "flex gap-4"}`}>
            {showCosts && (
              <p className="text-xs font-bold text-gray-400 reseller-cost">
                preco de revenda:{" "}
                {(
                  pc.price.promotional_reseller_price ??
                  pc.price.resell ??
                  0
                ).toLocaleString("pt-PT", {
                  style: "currency",
                  currency: pc.price.currency,
                })}{" "}
                {pc.price.promotional_reseller_price != null &&
                  pc.price.resell != null &&
                  pc.price.promotional_reseller_price < pc.price.resell && (
                    <del className="ml-1" title="Preço anterior">
                      {pc.price.resell.toLocaleString("pt-PT", {
                        style: "currency",
                        currency: pc.price.currency,
                      })}
                    </del>
                  )}
              </p>
            )}
            {pc.price.promotional_pvp_with_vat != null &&
            pc.price.promotional_pvp_with_vat > 0 ? (
              <div className="text-lg font-bold price-value">
                <span className="text-green-700">
                  {pc.price.promotional_pvp_with_vat.toLocaleString("pt-PT", {
                    style: "currency",
                    currency: pc.price.currency,
                  })}
                </span>
                <span className="text-xs text-gray-400 ml-1 line-through">
                  {pc.price.value.toLocaleString("pt-PT", {
                    style: "currency",
                    currency: pc.price.currency,
                  })}
                </span>
              </div>
            ) : (
              <p className="text-lg font-bold price-value">
                {pc.price.value.toLocaleString("pt-PT", {
                  style: "currency",
                  currency: pc.price.currency,
                })}
              </p>
            )}
          </div>
        </div>
        <div className="absolute items-center justify-between bottom-0 right-0 p-1 is-clearfix">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              handleAddToCompare(pc.id);
              e.stopPropagation();
            }}
            disabled={
              !selectedPCIdsToCompare.includes(pc.id) &&
              selectedPCIdsToCompare.length >= 5
            }
            className={`is-clickable p-2  text-white  disabled:bg-gray-400 ${
              selectedPCIdsToCompare.includes(pc.id)
                ? "bg-blue-500 hover:bg-red-600"
                : "bg-gray-800 hover:bg-blue-700"
            }`}
            aria-label="Adicionar ao comparador"
            title="Adicionar ao comparador"
          >
            {selectedPCIdsToCompare.includes(pc.id) ? (
              <GitCompare className="w-5 h-5" />
            ) : (
              <Scale className="w-5 h-5" />
            )}
          </motion.button>
        </div>
      </Card.Content>
    </Card>
  );
};

export default PCListItem;
